import React, { memo, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '../../../aem-core-components';
import Checkbox from '../../global/atoms/Checkbox/Checkbox.js';
import { getBasicAccountScreenConfigs, getCashToPodScreenConfigs } from '../utils/guidedUtil';
import { BUSINESS_ACCOUNT, CCA_PATH, LOGIN_SCREEN, POD_PATH } from '../constants';
import { LooksLikeTestId } from '../GRTestId';
import CheckboxIcon from '../../../resources/images/checkmark-wavy-green.svg';
import AlertIcon from '../../../resources/images/alert-triangle-red.svg';
import AccountModal from './accountModal';
import OktaModal from '../../global/modules/oktaModal/OktaModal';
import { useCheckUser } from '../../../hooks/useCheckUser.js';
import { USER_TYPE } from '../../../constants/userDetailsConstants.js';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';
import config from '../../App/config.js';
import './looksLike.scss';
import { isPODFlow } from '../../cca/util/ccaUtil';

const LooksLike = () => {
    const [isChecked, setIsChecked] = useState(false);
    const basicAccountScreenConfigs = getBasicAccountScreenConfigs();
    const cashToPodScreenConfigs = getCashToPodScreenConfigs();
    const intl = useIntl();
    const userType = useCheckUser();
    const {
        sendEventsForPageViewed,
        sendEventsForGuidedRegistrationFormViewed,
        sendEventsForGuidedRegistrationFormCompleted
    } = useAnalyticsContext();
    const [isOktaModalOpen, setIsOktaModalOpen] = useState(false);
    const isPODFlowSelected = isPODFlow()
    const openOktaModal = () => {
        setIsOktaModalOpen(!isOktaModalOpen);
    };
    const handleCloseOkta = () => {
        setIsOktaModalOpen(!isOktaModalOpen);
    };

    const lookLikeErrorMsg = {
        lookLikeMessage: intl.formatMessage({ id: 'look_like_err_message' })
    };

    useEffect(() => {
        sendEventsForPageViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PAGE_VIEWED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_CONVERT_PROFILE,
            window.location.href
        );
        sendEventsForGuidedRegistrationFormViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_VIEWED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_CONVERT_PROFILE,
            BUSINESS_ACCOUNT
        );
    }, []);

    const {
        basicAccountTitleLabel = '',
        basicAccountScreenSubTitle = '',
        basicAccountDescription = '',
        creditAccountFeatures = '',
        applyNowButtonLabel = '',
        skipAndLoginCTA = '',
        standardAndCreditAccountDescription = '',
        standardAndCreditAccountTitle = '',
        understandCheckboxLabel = ''
    } = (isPODFlowSelected ? cashToPodScreenConfigs : basicAccountScreenConfigs) || {};

    const [formError, setFormError] = useState('');

    const handleSubmit = event => {
        event.preventDefault();
    };

    const getCheckboxError = () => {
        return !isChecked ? lookLikeErrorMsg.lookLikeMessage : '';
    };

    const onContinue = () => {
        const checkboxError = getCheckboxError();
        if (checkboxError) {
            setFormError(checkboxError);
        } else {
            setFormError('');
            sendEventsForGuidedRegistrationFormCompleted(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_COMPLETED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_CONVERT_PROFILE,
                BUSINESS_ACCOUNT,
                applyNowButtonLabel
            );
            if (userType === USER_TYPE.GUEST) {
                openOktaModal();
            } else {
                // redirect to cca or pod based on user payment preference selection 
                window.location.href = isPODFlowSelected ? POD_PATH :  CCA_PATH;
            }
        }
    };

    const handleCheckbox = () => {
        setIsChecked(!isChecked);
        setFormError('');
    };

    const sendAnalytics = () => {
        sendEventsForGuidedRegistrationFormCompleted(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_COMPLETED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_CONVERT_PROFILE,
            BUSINESS_ACCOUNT,
            skipAndLoginCTA
        );
    };

    return (
        <section className="looks_like__section">
            <div className="container">
                <div className="row looks_like__row">
                    <div className="col-lg-6">
                        <div className="form-heading looks_like__heading">
                            <h2 className="looks_like__title" data-testid={LooksLikeTestId.LOOKS_LIKE_TITLE}>
                                {basicAccountTitleLabel}
                            </h2>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <fieldset className="looks_like__fieldset">
                                <div className="looks_like__content">
                                    <div>
                                        <div
                                            className="looks_like__subtitle"
                                            data-testid={LooksLikeTestId.LOOKS_LIKE_SUBTITLE}>
                                            {basicAccountScreenSubTitle}
                                        </div>
                                        <small
                                            className="looks_like__heading"
                                            data-testid={LooksLikeTestId.LOOKS_LIKE_DESCRIPTION}>
                                            {basicAccountDescription}
                                        </small>

                                        {Array.isArray(creditAccountFeatures) &&
                                            creditAccountFeatures.map((feature, index) => (
                                                <div key={index} className="looks_like__data">
                                                    <CheckboxIcon />
                                                    <small
                                                        className="looks_like__list"
                                                        data-testid={LooksLikeTestId.LOOKS_LIKE_LISTS}>
                                                        {feature}
                                                    </small>
                                                </div>
                                            ))}

                                        <hr className="looks_like__divider" />

                                        <AccountModal />

                                        <p className="looks_like__msg" data-testid={LooksLikeTestId.ACCOUNT_DIES}>
                                            {standardAndCreditAccountDescription}
                                        </p>

                                        <div
                                            className={`looks_like__checkbox ${formError ? 'checkbox_error' : ''}`}>
                                            <Checkbox
                                                isChecked={isChecked}
                                                dataTestId = {LooksLikeTestId.CHECKBOX}
                                                onClick={handleCheckbox}
                                                label={understandCheckboxLabel}
                                                customClassName={formError ? 'checkbox_error' : ''}
                                            />
                                        </div>
                                        {formError && (
                                            <div className="looks_like__error_account">
                                                <AlertIcon />
                                                <span className="looks_like__error_input">{formError}</span>
                                            </div>
                                        )}
                                        <Button
                                            data-testid={LooksLikeTestId.CONTINUE_BUTTON}
                                            className="button button-primary button-block looks_like__btn"
                                            type="button"
                                            onClick={onContinue}
                                            buttonAriaLabel="Continue">
                                            {applyNowButtonLabel}
                                        </Button>
                                    </div>
                                </div>
                            </fieldset>
                        </form>

                        <a href={LOGIN_SCREEN} onClick={sendAnalytics} className="looks_like__skip" data-testid={LooksLikeTestId.LOOKS_SKIP}>
                            {skipAndLoginCTA}
                        </a>

                        <OktaModal
                            isOpen={isOktaModalOpen}
                            urlPath={isPODFlowSelected ? config.pagePaths.podGuidedSignInIframe : config.pagePaths.guidedSignInIframe }
                            handleRequestClose={handleCloseOkta}
                            handleCloseOkta={handleCloseOkta}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default memo(LooksLike);
